import React, { useState, useEffect } from 'react';
import {
  DollarSign,
  ShoppingCart,
  Users,
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
  BarChart3,
  PieChart,
  Zap,
  Target,
  RefreshCw,
  Wifi,
  WifiOff,
  CheckCircle2,
  XCircle,
  Calendar,
  TrendingDown,
  Truck,
  Receipt,
  Tag,
  ArrowUp,
  ArrowDown,
  Minus,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';

// ─── Platform Komisyon Oranları (%%) ────────────────────────────────────────
const PLATFORM_COMMISSION_RATES: Record<string, number> = {
  trendyol: 15,
  hepsiburada: 13,
  ciceksepeti: 20,
  pttavm: 12,
  n11: 15,
  amazon: 15,
  beymen: 20,
  shopify: 3,
  woocommerce: 3,
  'google-analytics': 0,
  'google-ads': 0,
  'meta-ads': 0,
};

const AVG_SHIPPING_COST_PER_ORDER = 28; // TL (sabit tahmini kargo maliyeti)

// ─── Türkiye E-Ticaret Takvimi 2026 ─────────────────────────────────────────
interface CalendarEvent {
  date: string;
  name: string;
  type: 'holiday' | 'campaign' | 'national';
  description: string;
}

const TURKEY_CALENDAR_2026: CalendarEvent[] = [
  { date: '2026-03-20', name: 'Ramazan Bayramı', type: 'holiday', description: 'Gıda, şekerleme, hediye kategorileri pik yapıyor' },
  { date: '2026-04-23', name: 'Ulusal Egemenlik ve Çocuk Bayramı', type: 'national', description: 'Oyuncak, çocuk kıyafeti kategorileri' },
  { date: '2026-05-01', name: 'İşçi Bayramı', type: 'national', description: 'Genel tatil — lojistik yavaşlar' },
  { date: '2026-05-10', name: 'Anneler Günü', type: 'campaign', description: 'Çiçek, takı, moda — yılın en yoğun kampanya günü' },
  { date: '2026-06-21', name: 'Babalar Günü', type: 'campaign', description: 'Elektronik, giyim, hediye kategorileri' },
  { date: '2026-06-26', name: 'Kurban Bayramı', type: 'holiday', description: '4 gün tatil — et, giyim, ev kategorileri pik yapar' },
  { date: '2026-09-01', name: 'Okul Sezonu', type: 'campaign', description: 'Kırtasiye, çanta, giyim — yüksek talep' },
  { date: '2026-10-29', name: 'Cumhuriyet Bayramı', type: 'national', description: 'Ulusal bayram' },
  { date: '2026-11-11', name: 'Singles Day (11.11)', type: 'campaign', description: 'Global indirim günü — elektronik, moda, ev' },
  { date: '2026-11-27', name: 'Black Friday', type: 'campaign', description: 'Yılın en büyük indirim günü — 2 hafta önceden hazırlık' },
  { date: '2026-11-30', name: 'Cyber Monday', type: 'campaign', description: 'Black Friday\'ı takip eden dijital kampanya günü' },
  { date: '2026-12-25', name: 'Yılbaşı Hazırlığı', type: 'campaign', description: 'Hediye, oyuncak, dekorasyon — Aralık boyunca yüksek satış' },
];

function getUpcomingEvents(daysAhead = 60): (CalendarEvent & { daysLeft: number })[] {
  const now = new Date();
  return TURKEY_CALENDAR_2026
    .map(ev => ({
      ...ev,
      daysLeft: Math.ceil((new Date(ev.date).getTime() - now.getTime()) / (1000 * 60 * 60 * 24)),
    }))
    .filter(ev => ev.daysLeft >= 0 && ev.daysLeft <= daysAhead)
    .sort((a, b) => a.daysLeft - b.daysLeft);
}

type TimeRange = 'today' | 'week' | 'month';

interface DashboardMetrics {
  totalRevenue: number;
  totalOrders: number;
  aov: number;
  sessions: number;
  lowStockCount: number;
  revenueChange: number;
  ordersChange: number;
}

interface PlatformBreakdown {
  key: string;
  name: string;
  revenue: number;
  orders: number;
  percentage: number;
  color: string;
}

interface DailyChartData {
  day: string;
  date: string;
  revenue: number;
  orders: number;
}

interface DashboardData {
  metrics: DashboardMetrics;
  platformBreakdown: PlatformBreakdown[];
  dailyChart: DailyChartData[];
  connectedPlatforms: string[];
  hasData: boolean;
  lastUpdated: string | null;
}

const EMPTY_METRICS: DashboardMetrics = {
  totalRevenue: 0,
  totalOrders: 0,
  aov: 0,
  sessions: 0,
  lowStockCount: 0,
  revenueChange: 0,
  ordersChange: 0,
};

interface CompetitorProduct {
  productId: string;
  title: string;
  myPrice: number;
  marketMin: number;
  marketMax: number;
  status: 'competitive' | 'expensive' | 'cheap';
}

const AnalyticsDashboard: React.FC = () => {
  const { user } = useAuth();
  const [timeRange, setTimeRange] = useState<TimeRange>('week');
  const [isLoading, setIsLoading] = useState(true);
  const [dashData, setDashData] = useState<DashboardData | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [animatedValues, setAnimatedValues] = useState({ revenue: 0, orders: 0, aov: 0, sessions: 0 });
  const [competitorData, setCompetitorData] = useState<CompetitorProduct[]>([]);
  const [isLoadingCompetitors, setIsLoadingCompetitors] = useState(false);

  const fetchCompetitorPrices = async () => {
    if (!user?.uid) return;
    setIsLoadingCompetitors(true);
    try {
      const res = await fetch(`${FUNCTIONS_URL}/getTrendyolCompetitorPrices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.uid, limit: 5 }),
      });
      if (res.ok) {
        const data = await res.json();
        setCompetitorData(data.products || []);
      }
    } catch { /* sessizce geç */ } finally {
      setIsLoadingCompetitors(false);
    }
  };

  const fetchData = async (range: TimeRange) => {
    if (!user?.uid) return;
    setIsLoading(true);
    setFetchError(null);
    try {
      const res = await fetch(`${FUNCTIONS_URL}/getDashboardData`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.uid, timeRange: range }),
      });
      if (!res.ok) throw new Error('Sunucu hatası');
      const data: DashboardData = await res.json();
      setDashData(data);
    } catch (err) {
      setFetchError('Dashboard verisi yüklenemedi. Lütfen tekrar deneyin.');
      console.error('getDashboardData hatası:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData(timeRange);
  }, [timeRange, user?.uid]);

  useEffect(() => {
    fetchCompetitorPrices();
  }, [user?.uid]);

  useEffect(() => {
    if (!isLoading && dashData) {
      const m = dashData.metrics;
      const duration = 1500;
      const steps = 60;
      let step = 0;
      const timer = setInterval(() => {
        step++;
        const easeOut = 1 - Math.pow(1 - step / steps, 3);
        setAnimatedValues({
          revenue: Math.round(m.totalRevenue * easeOut),
          orders: Math.round(m.totalOrders * easeOut),
          aov: Math.round(m.aov * easeOut),
          sessions: Math.round(m.sessions * easeOut),
        });
        if (step >= steps) clearInterval(timer);
      }, duration / steps);
      return () => clearInterval(timer);
    }
  }, [isLoading, dashData]);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);

  const getMaxChartValue = () =>
    Math.max(...(dashData?.dailyChart.map(d => d.revenue) ?? []), 1);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Veriler yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-gray-400 mb-4">{fetchError}</p>
          <button
            onClick={() => fetchData(timeRange)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-xl hover:bg-purple-600 transition-colors mx-auto"
          >
            <RefreshCw className="w-4 h-4" />
            Tekrar Dene
          </button>
        </div>
      </div>
    );
  }

  const metrics = dashData?.metrics ?? EMPTY_METRICS;
  const platformBreakdown = dashData?.platformBreakdown ?? [];
  const dailyChart = dashData?.dailyChart ?? [];
  const hasData = dashData?.hasData ?? false;
  const connectedPlatforms = dashData?.connectedPlatforms ?? [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <BarChart3 className="w-7 h-7 text-purple-400" />
            Analitik Dashboard
          </h1>
          <p className="text-gray-400 mt-1">
            {dashData?.lastUpdated
              ? `Son güncelleme: ${new Date(dashData.lastUpdated).toLocaleString('tr-TR')}`
              : 'E-ticaret performansınızın özeti'}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchData(timeRange)}
            className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-gray-400 hover:text-white"
            title="Yenile"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2 bg-[#1a1a2e] rounded-xl p-1">
            {(['today', 'week', 'month'] as TimeRange[]).map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  timeRange === range
                    ? 'bg-purple-500 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                {range === 'today' ? 'Bugün' : range === 'week' ? 'Bu Hafta' : 'Bu Ay'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Revenue */}
        <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/5 rounded-2xl p-5 border border-green-500/20">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-green-400" />
            </div>
            <div className={`flex items-center gap-1 text-sm font-medium ${metrics.revenueChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {metrics.revenueChange >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              {Math.abs(metrics.revenueChange)}%
            </div>
          </div>
          <p className="text-gray-400 text-sm mb-1">Toplam Gelir</p>
          <p className="text-2xl font-bold text-white">{formatCurrency(animatedValues.revenue)}</p>
        </div>

        {/* Total Orders */}
        <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/5 rounded-2xl p-5 border border-blue-500/20">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <ShoppingCart className="w-5 h-5 text-blue-400" />
            </div>
            <div className={`flex items-center gap-1 text-sm font-medium ${metrics.ordersChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {metrics.ordersChange >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              {Math.abs(metrics.ordersChange)}%
            </div>
          </div>
          <p className="text-gray-400 text-sm mb-1">Toplam Sipariş</p>
          <p className="text-2xl font-bold text-white">{animatedValues.orders}</p>
        </div>

        {/* Average Order Value */}
        <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/5 rounded-2xl p-5 border border-purple-500/20">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
              <Target className="w-5 h-5 text-purple-400" />
            </div>
            <span className="text-xs text-gray-500 bg-white/5 px-2 py-1 rounded-lg">AOV</span>
          </div>
          <p className="text-gray-400 text-sm mb-1">Ort. Sipariş Değeri</p>
          <p className="text-2xl font-bold text-white">{formatCurrency(animatedValues.aov)}</p>
        </div>

        {/* Sessions (GA4) */}
        <div className="bg-gradient-to-br from-orange-500/10 to-amber-500/5 rounded-2xl p-5 border border-orange-500/20">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-orange-400" />
            </div>
            <span className="text-xs text-gray-500 bg-white/5 px-2 py-1 rounded-lg">GA4</span>
          </div>
          <p className="text-gray-400 text-sm mb-1">Site Oturumları</p>
          <p className="text-2xl font-bold text-white">
            {metrics.sessions > 0 ? animatedValues.sessions.toLocaleString('tr-TR') : '—'}
          </p>
          {metrics.sessions === 0 && (
            <p className="text-xs text-gray-600 mt-1">Google Analytics bağlı değil</p>
          )}
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sales Chart */}
        <div className="lg:col-span-2 bg-[#1a1a2e]/50 rounded-2xl p-5 border border-white/5">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-white">Satış Grafiği</h3>
              <p className="text-sm text-gray-400">Günlük satış performansı</p>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-purple-500" />
                <span className="text-gray-400">Gelir</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500" />
                <span className="text-gray-400">Sipariş</span>
              </div>
            </div>
          </div>

          {dailyChart.length > 0 && hasData ? (
            <div className="relative h-64">
              <div className="absolute inset-0 flex items-end justify-between gap-2 pb-8">
                {dailyChart.map((day) => {
                  const maxOrders = Math.max(...dailyChart.map(d => d.orders), 1);
                  const heightPercent = (day.revenue / getMaxChartValue()) * 100;
                  return (
                    <div key={day.date} className="flex-1 flex flex-col items-center gap-2 group">
                      <div className="relative w-full flex justify-center gap-1">
                        <div
                          className="w-5 bg-gradient-to-t from-purple-600 to-purple-400 rounded-t-lg transition-all duration-500 hover:from-purple-500 hover:to-purple-300 cursor-pointer"
                          style={{ height: `${Math.max(heightPercent * 2, 2)}px` }}
                        >
                          <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-[#12121a] px-2 py-1 rounded text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap border border-white/10">
                            {formatCurrency(day.revenue)}
                          </div>
                        </div>
                        <div
                          className="w-5 bg-gradient-to-t from-blue-600 to-blue-400 rounded-t-lg transition-all duration-500"
                          style={{ height: `${Math.max((day.orders / maxOrders) * 200, 2)}px` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="absolute bottom-0 left-0 right-0 flex justify-between">
                {dailyChart.map((day) => (
                  <div key={day.date} className="flex-1 text-center text-sm text-gray-500">
                    {day.day}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-64 flex flex-col items-center justify-center text-center">
              <BarChart3 className="w-12 h-12 text-gray-700 mb-3" />
              <p className="text-gray-500 text-sm">Henüz satış verisi yok</p>
              <p className="text-gray-600 text-xs mt-1">Platform bağlayın ve veri sync bekleyin</p>
            </div>
          )}
        </div>

        {/* Platform Distribution */}
        <div className="bg-[#1a1a2e]/50 rounded-2xl p-5 border border-white/5">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-white">Platform Dağılımı</h3>
              <p className="text-sm text-gray-400">Gelir bazlı</p>
            </div>
            <PieChart className="w-5 h-5 text-gray-500" />
          </div>

          {platformBreakdown.length > 0 ? (
            <>
              <div className="relative w-40 h-40 mx-auto mb-6">
                <svg className="w-full h-full transform -rotate-90">
                  {platformBreakdown.reduce((acc, platform, index) => {
                    const prevPct = platformBreakdown.slice(0, index).reduce((sum, p) => sum + p.percentage, 0);
                    const circumference = 2 * Math.PI * 60;
                    acc.push(
                      <circle
                        key={platform.key}
                        cx="80" cy="80" r="60"
                        fill="none"
                        stroke={platform.color}
                        strokeWidth="20"
                        strokeDasharray={`${(platform.percentage / 100) * circumference} ${circumference}`}
                        strokeDashoffset={-((prevPct / 100) * circumference)}
                        className="transition-all duration-1000"
                      />
                    );
                    return acc;
                  }, [] as React.ReactNode[])}
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <p className="text-xl font-bold text-white">{formatCurrency(metrics.totalRevenue)}</p>
                  <p className="text-xs text-gray-400">Toplam</p>
                </div>
              </div>
              <div className="space-y-3">
                {platformBreakdown.map((platform) => (
                  <div key={platform.key} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: platform.color }} />
                      <span className="text-sm text-gray-300">{platform.name}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-medium text-white">{formatCurrency(platform.revenue)}</span>
                      <span className="text-xs text-gray-500 ml-2">({platform.percentage}%)</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-48 text-center">
              <PieChart className="w-12 h-12 text-gray-700 mb-3" />
              <p className="text-gray-500 text-sm">Platform verisi yok</p>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Stock Alerts */}
        <div className="bg-[#1a1a2e]/50 rounded-2xl p-5 border border-white/5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Stok Uyarıları</h3>
              <p className="text-sm text-gray-400">
                {metrics.lowStockCount > 0
                  ? `${metrics.lowStockCount} ürün kritik seviyede`
                  : 'Kritik stok uyarısı yok'}
              </p>
            </div>
          </div>

          {metrics.lowStockCount > 0 ? (
            <div className="flex items-center gap-3 p-4 bg-red-500/10 rounded-xl border border-red-500/20">
              <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-white">{metrics.lowStockCount} ürün kritik stok seviyesinde</p>
                <p className="text-xs text-gray-500 mt-1">AI Analiz sayfasında detayları görüntüleyebilirsiniz</p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-32 text-center">
              <CheckCircle2 className="w-10 h-10 text-green-500/40 mb-2" />
              <p className="text-gray-500 text-sm">
                {connectedPlatforms.length > 0 ? 'Tüm ürünler yeterli stokta' : 'Platform bağlantısı bekleniyor'}
              </p>
            </div>
          )}
        </div>

        {/* Connected Platforms */}
        <div className="bg-[#1a1a2e]/50 rounded-2xl p-5 border border-white/5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <Wifi className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Bağlı Platformlar</h3>
              <p className="text-sm text-gray-400">{connectedPlatforms.length} aktif bağlantı</p>
            </div>
          </div>

          {connectedPlatforms.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {connectedPlatforms.map((platform) => (
                <div
                  key={platform}
                  className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded-xl border border-white/10"
                >
                  <div className="w-2 h-2 rounded-full bg-green-400" />
                  <span className="text-sm text-gray-300 capitalize">{platform.replace(/-/g, ' ')}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-32 text-center">
              <WifiOff className="w-10 h-10 text-gray-700 mb-2" />
              <p className="text-gray-500 text-sm">Henüz bağlı platform yok</p>
            </div>
          )}
        </div>
      </div>

      {/* Kârlılık Hesabı + Türkiye Takvimi */}
      {(() => {
        const upcomingEvents = getUpcomingEvents(60);
        const avgCommission = connectedPlatforms.length > 0
          ? connectedPlatforms.reduce((sum, p) => {
              const key = p.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z-]/g, '');
              const rate = Object.entries(PLATFORM_COMMISSION_RATES).find(([k]) => key.includes(k))?.[1] ?? 13;
              return sum + rate;
            }, 0) / connectedPlatforms.filter(p => {
              const k = p.toLowerCase();
              return !k.includes('google') && !k.includes('meta');
            }).length || 13
          : 13;
        const grossRevenue = metrics.totalRevenue;
        const commissionCost = Math.round(grossRevenue * (avgCommission / 100));
        const shippingCost = Math.round(metrics.totalOrders * AVG_SHIPPING_COST_PER_ORDER);
        const netProfit = grossRevenue - commissionCost - shippingCost;
        const netMargin = grossRevenue > 0 ? Math.round((netProfit / grossRevenue) * 100) : 0;

        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Kârlılık Hesabı */}
            <div className="bg-[#1a1a2e]/50 rounded-2xl p-5 border border-white/5">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                  <Receipt className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Kârlılık Hesabı</h3>
                  <p className="text-sm text-gray-400">Gelir − Komisyon − Kargo = Net Kâr</p>
                </div>
              </div>

              {grossRevenue > 0 ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2 border-b border-white/5">
                    <div className="flex items-center gap-2 text-sm text-gray-300">
                      <DollarSign className="w-4 h-4 text-green-400" />
                      Brüt Gelir
                    </div>
                    <span className="text-sm font-semibold text-green-400">{formatCurrency(grossRevenue)}</span>
                  </div>

                  <div className="flex items-center justify-between py-2 border-b border-white/5">
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <TrendingDown className="w-4 h-4 text-orange-400" />
                      Platform Komisyonu
                      <span className="text-xs bg-orange-500/10 text-orange-400 px-1.5 py-0.5 rounded">~%{Math.round(avgCommission)}</span>
                    </div>
                    <span className="text-sm font-medium text-orange-400">−{formatCurrency(commissionCost)}</span>
                  </div>

                  <div className="flex items-center justify-between py-2 border-b border-white/5">
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <Truck className="w-4 h-4 text-blue-400" />
                      Tahmini Kargo
                      <span className="text-xs bg-blue-500/10 text-blue-400 px-1.5 py-0.5 rounded">~28₺/sipariş</span>
                    </div>
                    <span className="text-sm font-medium text-blue-400">−{formatCurrency(shippingCost)}</span>
                  </div>

                  <div className="flex items-center justify-between pt-3 mt-1">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                        <Receipt className="w-4 h-4 text-emerald-400" />
                      </div>
                      <span className="text-sm font-semibold text-white">Tahmini Net Kâr</span>
                    </div>
                    <div className="text-right">
                      <p className={`text-lg font-bold ${netProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {formatCurrency(netProfit)}
                      </p>
                      <p className="text-xs text-gray-500">Kar marjı: %{netMargin}</p>
                    </div>
                  </div>

                  <p className="text-xs text-gray-600 pt-2 border-t border-white/5">
                    * Reklam harcaması dahil değil. Tahmini değerler.
                  </p>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-40 text-center">
                  <Receipt className="w-10 h-10 text-gray-700 mb-2" />
                  <p className="text-gray-500 text-sm">Hesaplama için satış verisi gerekli</p>
                  <p className="text-gray-600 text-xs mt-1">Platform bağlayıp veri sync bekleyin</p>
                </div>
              )}
            </div>

            {/* Rakip Fiyat Analizi */}
            <div className="bg-[#1a1a2e]/50 rounded-2xl p-5 border border-white/5">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
                    <Tag className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Rakip Fiyat Analizi</h3>
                    <p className="text-sm text-gray-400">Trendyol — en son güncelleme</p>
                  </div>
                </div>
                <button
                  onClick={fetchCompetitorPrices}
                  disabled={isLoadingCompetitors}
                  className="p-2 rounded-lg hover:bg-white/5 transition-colors"
                >
                  <RefreshCw className={`w-4 h-4 text-gray-400 ${isLoadingCompetitors ? 'animate-spin' : ''}`} />
                </button>
              </div>

              {isLoadingCompetitors ? (
                <div className="flex items-center justify-center h-40">
                  <div className="w-6 h-6 border-2 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" />
                </div>
              ) : competitorData.length > 0 ? (
                <div className="space-y-2">
                  {competitorData.map((product) => {
                    const diffPct = product.marketMin > 0
                      ? Math.round(((product.myPrice - product.marketMin) / product.marketMin) * 100)
                      : 0;
                    const statusConfig = {
                      competitive: { label: 'Rekabetçi', color: 'text-green-400', bg: 'bg-green-500/10', Icon: Minus },
                      expensive: { label: 'Pahalı', color: 'text-red-400', bg: 'bg-red-500/10', Icon: ArrowUp },
                      cheap: { label: 'Ucuz', color: 'text-blue-400', bg: 'bg-blue-500/10', Icon: ArrowDown },
                    }[product.status];
                    return (
                      <div key={product.productId} className="flex items-center gap-3 p-2.5 rounded-xl bg-white/3 hover:bg-white/5 transition-colors">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white truncate">{product.title}</p>
                          <p className="text-xs text-gray-500">Piyasa: {product.marketMin.toLocaleString('tr-TR')}₺ – {product.marketMax.toLocaleString('tr-TR')}₺</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm font-semibold text-white">{product.myPrice.toLocaleString('tr-TR')}₺</p>
                          <span className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded ${statusConfig.bg} ${statusConfig.color}`}>
                            <statusConfig.Icon className="w-3 h-3" />
                            {diffPct !== 0 ? `%${Math.abs(diffPct)} ` : ''}{statusConfig.label}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-40 text-center">
                  <Tag className="w-10 h-10 text-gray-700 mb-2" />
                  <p className="text-gray-500 text-sm">Rakip veri yok</p>
                  <p className="text-gray-600 text-xs mt-1">Trendyol bağlantısı ve ürün gerekli</p>
                </div>
              )}
            </div>

            {/* Türkiye Takvimi */}
            <div className="bg-[#1a1a2e]/50 rounded-2xl p-5 border border-white/5">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Yaklaşan Kampanya Günleri</h3>
                  <p className="text-sm text-gray-400">Sonraki 60 gün</p>
                </div>
              </div>

              {upcomingEvents.length > 0 ? (
                <div className="space-y-2.5">
                  {upcomingEvents.slice(0, 5).map((ev) => {
                    const typeColors: Record<string, string> = {
                      campaign: 'bg-amber-500/10 border-amber-500/20 text-amber-400',
                      holiday: 'bg-green-500/10 border-green-500/20 text-green-400',
                      national: 'bg-blue-500/10 border-blue-500/20 text-blue-400',
                    };
                    const typeLabels: Record<string, string> = {
                      campaign: 'Kampanya',
                      holiday: 'Bayram',
                      national: 'Milli',
                    };
                    const urgency = ev.daysLeft <= 7
                      ? 'border-red-500/30 bg-red-500/5'
                      : ev.daysLeft <= 14
                      ? 'border-orange-500/20 bg-orange-500/5'
                      : 'border-white/5 bg-white/2';

                    return (
                      <div key={ev.date} className={`flex items-start gap-3 p-3 rounded-xl border ${urgency}`}>
                        <div className="text-center flex-shrink-0 w-12">
                          <p className={`text-lg font-bold ${ev.daysLeft <= 7 ? 'text-red-400' : ev.daysLeft <= 14 ? 'text-orange-400' : 'text-white'}`}>
                            {ev.daysLeft}
                          </p>
                          <p className="text-xs text-gray-500">gün</p>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-medium text-white">{ev.name}</p>
                            <span className={`text-xs px-1.5 py-0.5 rounded border ${typeColors[ev.type]}`}>
                              {typeLabels[ev.type]}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5 truncate">{ev.description}</p>
                        </div>
                      </div>
                    );
                  })}
                  {upcomingEvents.length > 5 && (
                    <p className="text-xs text-gray-500 text-center pt-1">
                      +{upcomingEvents.length - 5} etkinlik daha
                    </p>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-40 text-center">
                  <Calendar className="w-10 h-10 text-gray-700 mb-2" />
                  <p className="text-gray-500 text-sm">Önümüzdeki 60 günde özel gün yok</p>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Connection CTA — only shown when no platforms connected */}
      {connectedPlatforms.length === 0 && (
        <div className="bg-gradient-to-r from-purple-500/10 via-blue-500/10 to-cyan-500/10 rounded-2xl p-6 border border-purple-500/20">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                <Zap className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Gerçek Verilerinizi Görün</h3>
                <p className="text-sm text-gray-400">Pazaryeri hesaplarınızı bağlayarak canlı verilere erişin</p>
              </div>
            </div>
            <button
              onClick={() => window.location.href = '/dashboard/connections'}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-xl font-medium hover:opacity-90 transition-opacity"
            >
              Bağlantıları Yönet
              <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
